import React from 'react';
import Button from '@material-ui/core/Button';
import {TimelineMarker} from 'VideoAudioQC/Timeline/TimelineTypes';
import {Item} from 'VideoAudioQC/QCMedia/Redux/QCMediaActions';
import {curry} from 'ramda';
import {OnMouseDivEvent} from '@pluto/@types';
import {msToTime} from '@pluto/utils';
import {getMetadataContentType} from 'QCTools/QCToolsMetadata/QCToolsMetadataContainer';
import {contentTypeRequiredFieldsConfig} from 'QCTools/QCToolsMetadata/QCToolsMetadataTypes';
import TableContainer, {OnButtonClick, OnStartFrameChange} from './Table/TableContainer';
import {AdBreakData} from './Table/Table';

import './AdBreaks.scss';

type OnClickOutsideList = () => void;

export interface AdBreaksProps {
    markers: TimelineMarker[];
    selectedMarkerId: string;
    adOnPlayingFrame?: TimelineMarker;
    item: Item;
    onCreateMarker: () => void;
    onMarkerClick: (marker: AdBreakData) => void;
    onEditMarkerClick: OnButtonClick;
    onGoToMarkerClick: OnButtonClick;
    onPreviewAdClick: OnButtonClick;
    onPreviewAllAdClick: () => void;
    onDeleteClick: OnButtonClick;
    onStartFrameChange: OnStartFrameChange;
    onClickOutsideList: OnClickOutsideList;
    allotment: number | null;
    contentType: string;
    videoData: {
        dropFrame: boolean,
        frameRateValue: number,
    },
    jobDeliveryDate: string;
}

export const onClickOnContainer = curry((onClickOutsideList: OnClickOutsideList, event: OnMouseDivEvent) => {
    event.stopPropagation();

    onClickOutsideList();
});

export const previewAllAdClick = curry((onPreviewAllAdClick, event: OnMouseDivEvent) => {
    event.stopPropagation();

    onPreviewAllAdClick();
});

export const createMarker = curry((onCreateMarker: () => void, event: OnMouseDivEvent) => {
    event.stopPropagation();

    onCreateMarker();
});

export default function AdBreaks(props: AdBreaksProps) {
    const {
        item,
        markers,
        selectedMarkerId,
        adOnPlayingFrame,
        onCreateMarker,
        onMarkerClick,
        onGoToMarkerClick,
        onEditMarkerClick,
        onDeleteClick,
        onPreviewAdClick,
        onPreviewAllAdClick,
        onStartFrameChange,
        onClickOutsideList,
        allotment,
        contentType,
        jobDeliveryDate
    } = props;
    const {
        frameRate,
        dropFrame,
        duration,
    } = item.video;

    const metadataContentType = getMetadataContentType(contentType);
    const requiredFields = contentTypeRequiredFieldsConfig[metadataContentType];
    const handleClickOutsideList = onClickOnContainer(onClickOutsideList);

    const handleOnPreviewAllAdClick = previewAllAdClick(onPreviewAllAdClick);

    const handleCreateMarker = createMarker(onCreateMarker);

    return (
        <div
            className="ad-break-root"
            aria-hidden="true"
            onClick={handleClickOutsideList}
        >
            <div
                className="button-container"
                aria-hidden="true"
                onClick={handleClickOutsideList}
            >
                <div className="allotment-container">
                    {
                        requiredFields.includes('allotment')
                                && (
                                    <>
                                        <span>Allotment:</span>
                                        <span>
                                            {msToTime(allotment as number)}
                                        </span>
                                    </>
                                )
                    }
                </div>
                <div>
                    <Button
                        onClick={handleOnPreviewAllAdClick}
                        variant="outlined"
                        size="small"
                    >
                        Preview All
                    </Button>
                    <Button
                        onClick={handleCreateMarker}
                        disabled={Boolean(jobDeliveryDate)}
                        variant="outlined"
                        color="primary"
                        size="small"
                    >
                        Add Breakpoint
                    </Button>
                </div>
            </div>
            <TableContainer
                markers={markers}
                jobDeliveryDate={jobDeliveryDate}
                selectedMarkerId={selectedMarkerId}
                adOnPlayingFrame={adOnPlayingFrame}
                frameRate={frameRate}
                dropFrame={dropFrame}
                duration={duration}
                onMarkerClick={onMarkerClick}
                onPreviewAdClick={onPreviewAdClick}
                onMoveClick={onEditMarkerClick}
                onGoToClick={onGoToMarkerClick}
                onDeleteClick={onDeleteClick}
                onStartFrameChange={onStartFrameChange}
            />
        </div>
    );
}
